/**
 * console_rentals.ts — the structure console's RENTALS section (the interiors programme, slice R, 2026-10-01).
 *
 * The landlord's view: every room the PLANNER drew in this building (the visit's own descriptor — the console stands
 * inside it), each with its state — not listed / to let / let to <tenant> — and its acts: list at a price with a
 * note, re-price for the next tenant, withdraw, give notice. What the client sends when it lists a room is the CLAIM
 * (`rental_client.unitClaimsOf`): the planner's rect, threshold and the room's keyed pieces, bound to the layout pin's
 * digest; the server bounds it and checks it against its own room plan. Everyone else sees what is to let.
 *
 * Built into the console's SOFT RENDER (rows pair by `data-row`, fields keep their typing by `data-k`); tokens only.
 */
import type { Identity } from 'spacetimedb';
import { enterables } from '../../lib/building_interiors.js';
import { activeInterior } from '../../lib/interior_wiring.js';
import { RENT_MIN_SOV, RENT_MAX_SOV, UNIT_NOTE_MAX, rentProblem, releaseSplit, type UnitClaim } from '../../lib/rentals.js';
import { unitClaimsOf, unitNumber, leaseStatus, leaseFacts } from '../../lib/rental_client.js';
import { unitsOfStructure, liveLeaseOfUnit, allReceipts, rentalReducers, structureRow, layoutPin } from './rental_rows.js';

export interface RentalsConsoleHost {
  structureId: bigint; structureType: string; interiorId: string | null; admin: boolean; isOwner: boolean; furnished: boolean;
  section(title: string): HTMLElement;
  row(...els: HTMLElement[]): HTMLElement;
  text(text: string, color: string): HTMLElement;
  input(value: string, placeholder: string, max: number): HTMLInputElement;
  button(label: string, onClick: () => void, enabled: boolean): HTMLButtonElement;
  live<T extends HTMLInputElement | HTMLSelectElement>(k: string, fallback: T): T;
  call(label: string, fn: () => Promise<unknown>): void;
  nameOf(id: Identity): string;
}

/** The interior's origin in the structure's own frame (the frame `structure_item.lx / lz` use). */
export function interiorOrigin(structureId: bigint, interiorId: string | null): { ox: number; oz: number } {
  const s = structureRow(structureId), b = interiorId ? enterables.get(interiorId) : undefined;
  if (!s || !b) return { ox: 0, oz: 0 };
  const dx = b.x - s.posX, dz = b.z - s.posZ, c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  const r2 = (v: number): number => Math.round(v * 100) / 100;
  return { ox: r2(dx * c - dz * sn), oz: r2(dx * sn + dz * c) };
}

/** The claims of the room the player stands in, and why there are none ('' = there are). */
export function consoleClaims(structureId: bigint, interiorId: string | null): { claims: UnitClaim[]; digest: string; why: string } {
  const act = activeInterior();
  if (!interiorId || !act || act.id !== interiorId || !act.descriptor || !act.view) return { claims: [], digest: '', why: 'The room\'s plan has not landed yet — a moment.' };
  const d = act.descriptor;
  if (act.layout < 1 || !act.keyedOk) return { claims: [], digest: '', why: 'This building is drawn by the classic layout, which has no separate rooms — use Refit (above) to take the current layout.' };
  const claims = unitClaimsOf(d, act.view, interiorOrigin(structureId, interiorId));
  if (claims.length === 0) return { claims, digest: d.digest, why: 'This building has no separate room to let (a hotel has guest rooms; a large house and a manor have a spare room).' };
  const pin = layoutPin(structureId);
  if (!pin || pin.digest !== d.digest) return { claims, digest: d.digest, why: 'This layout is not stamped on the building yet (the owner\'s first visit stamps it) — step out and back in, then list.' };
  return { claims, digest: d.digest, why: '' };
}

export function buildRentalsSection(h: RentalsConsoleHost): HTMLElement | null {
  if (h.structureType === 'guild_hall') return null;
  const sec = h.section('RENTALS — let a room: rent is credits a day, a day\'s rent is yours when that day starts');
  const r = rentalReducers();
  const rows = unitsOfStructure(h.structureId);
  const nowS = Date.now() / 1000;
  if (!h.admin) {
    const open = rows.filter((u) => u.listed && !liveLeaseOfUnit(u.unitRowId));
    if (rows.length === 0) return null;
    sec.appendChild(h.text(open.length ? `To let here: ${open.map((u) => `room ${unitNumber(u.unitId)} at ${u.rentPerDay} ⓒ a day`).join(' · ')} — walk to the room's door and press E.` : 'Every room here is taken.', 'var(--ax-soft)'));
    return sec;
  }
  if (!h.furnished) { sec.appendChild(h.text('Furnish the building first — a room is let with its furniture.', 'var(--ax-muted)')); return sec; }
  const { claims, digest, why } = consoleClaims(h.structureId, h.interiorId);
  if (why) { const w = h.text(why, 'var(--ax-muted)'); w.setAttribute('data-k', 'rent:why'); sec.appendChild(w); }
  let listed = 0, letN = 0;
  // every planned room, then any listed room the plan no longer names (it can only be withdrawn)
  const ids = [...new Set([...claims.map((c) => c.unitId), ...rows.map((u) => u.unitId)])].sort();
  for (const unitId of ids) {
    const claim = claims.find((c) => c.unitId === unitId) ?? null;
    const row = rows.find((u) => u.unitId === unitId) ?? null;
    const lease = row ? liveLeaseOfUnit(row.unitRowId) : null;
    if (row?.listed) listed++;
    if (lease) letN++;
    const num = unitNumber(unitId);
    const box = document.createElement('div');
    box.setAttribute('data-row', `rent:${unitId}`);
    box.style.cssText = 'display:flex;flex-direction:column;gap:4px;padding:6px 8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius)';
    const pieces = claim ? claim.keys.map((k) => (k.split(':').pop() ?? '').replace(/_/g, ' ')).join(', ') : '';
    const state = lease
      ? `let to ${lease.tenantName} at ${lease.rentPerDay} ⓒ a day — ${leaseStatus(leaseFacts(lease), nowS).line}`
      : row?.listed ? `to let at ${row.rentPerDay} ⓒ a day${row.notes ? ` — "${row.notes}"` : ''}` : 'not listed';
    const headLine = h.text(`Room ${num} · floor ${claim?.floor ?? row?.floor ?? 0}${claim ? ` · ${claim.kind}` : ''} — ${state}`, lease ? 'var(--ax-good)' : row?.listed ? 'var(--ax-text)' : 'var(--ax-soft)');
    headLine.setAttribute('data-k', `rent:state:${unitId}`);
    box.appendChild(headLine);
    if (pieces) box.appendChild(h.text(`with: ${pieces}`, 'var(--ax-muted)'));
    const rentIn = h.input(row ? String(row.rentPerDay) : '', `${RENT_MIN_SOV}–${RENT_MAX_SOV} ⓒ a day`, 6);
    rentIn.type = 'number'; rentIn.min = String(RENT_MIN_SOV); rentIn.max = String(RENT_MAX_SOV); rentIn.style.flex = '0 0 130px'; rentIn.style.minWidth = '110px';
    rentIn.setAttribute('data-k', `rent:price:${unitId}`);
    const noteIn = h.input(row?.notes ?? '', 'a note for the door (optional)', UNIT_NOTE_MAX);
    noteIn.setAttribute('data-k', `rent:note:${unitId}`);
    const submit = (): void => {
      if (!claim || !r) return;
      const price = Math.floor(Number(h.live(`rent:price:${unitId}`, rentIn).value));
      const bad = rentProblem(price);
      if (bad) { h.call('', () => Promise.reject(new Error(bad))); return; }
      const split = releaseSplit(price, 1);
      h.call(`Room ${num} ${lease ? 're-priced for the next tenant' : 'is to let'} at ${price} ⓒ a day (${split.toOwner} to you, ${split.fee} fee).`, () => r.listRentalUnit({
        structureId: h.structureId, unitId, rentPerDay: price, notes: h.live(`rent:note:${unitId}`, noteIn).value, digest, kind: claim.kind, floor: claim.floor,
        x0: claim.x0, z0: claim.z0, x1: claim.x1, z1: claim.z1, tx: claim.tx, tz: claim.tz, ax: claim.ax, az: claim.az, ox: claim.ox, oz: claim.oz, keys: claim.keys,
      }));
    };
    const listBtn = h.button(lease ? 'Re-price (next tenant)' : row?.listed ? 'Update' : 'List to let', submit, !!claim && !why && !!r);
    listBtn.setAttribute('data-k', `rent:list:${unitId}`);
    const acts: HTMLElement[] = [rentIn, noteIn, listBtn];
    if (row && !lease) {
      const un = h.button('Withdraw', () => { if (r) h.call(`Room ${num} is no longer to let.`, () => r.unlistRentalUnit({ structureId: h.structureId, unitId })); }, !!r);
      un.setAttribute('data-k', `rent:unlist:${unitId}`);
      acts.push(un);
    }
    if (lease && h.isOwner) {
      const noticed = lease.noticeAt.microsSinceUnixEpoch !== 0n;
      const ev = h.button(noticed ? 'Notice given' : 'Give notice', () => { if (r) h.call(`Notice given — room ${num}'s lease ends at its next day boundary at least 24 h on; the tenant's goods go back to the tenant.`, () => r.evictTenant({ leaseId: lease.leaseId })); }, !noticed && !!r);
      ev.setAttribute('data-k', `rent:evict:${unitId}`);
      acts.push(ev);
    }
    box.appendChild(h.row(...acts));
    sec.appendChild(box);
  }
  const sum = h.text(`${listed} of ${ids.length} room${ids.length === 1 ? '' : 's'} listed · ${letN} let. Under a lease the room is the tenant's: its storage and bed are theirs, and a redeed, a refit or a new pack waits for the lease to end.`, 'var(--ax-muted)');
  sum.setAttribute('data-k', 'rent:sum');
  sec.appendChild(sum);
  // The landlord's receipts (row-filtered to the tenant and the landlord): the last few of this building.
  const rec = allReceipts().filter((x) => x.structureId === h.structureId).sort((a, b) => (a.receiptId < b.receiptId ? 1 : -1)).slice(0, 5);
  for (const x of rec) {
    const line = h.text(`room ${unitNumber(x.unitId)} · ${x.kind}${x.amountSov > 0n ? ` · ${x.amountSov} ⓒ` : ''}${x.feeSov > 0n ? ` (fee ${x.feeSov})` : ''} — ${x.detail}`, 'var(--ax-muted)');
    line.setAttribute('data-row', `rent:receipt:${x.receiptId}`);
    sec.appendChild(line);
  }
  return sec;
}
